import { type V2, dist } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { CharacterSave } from '../../game/character';
import { type LootDrop, rollLoot } from '../../game/loot';
import { CANNON_BLAST_RADIUS, WRECK_RESPAWN_MS, WRECK_SITES, type WreckKind, type WreckSite } from './data';

/**
 * Salvage: which wrecks a character can plunder right now (respawns are
 * remembered in character flags, so reloading doesn't reset them), what they
 * yield, and which ones a cannonball passes close enough to blast open.
 */

const flagKey = (id: string): string => `sailWreck_${id}`;

/** Time (ms) when a salvaged site drifts back, or 0 when it is available. */
export function wreckRespawnAt(char: CharacterSave | null | undefined, site: WreckSite, now = Date.now()): number {
  const v = char?.flags[flagKey(site.id)];
  return typeof v === 'number' && v > now ? v : 0;
}

export const wreckAvailable = (char: CharacterSave | null | undefined, site: WreckSite, now = Date.now()): boolean => wreckRespawnAt(char, site, now) === 0;

export function availableWrecks(char: CharacterSave | null | undefined, now = Date.now(), sites: readonly WreckSite[] = WRECK_SITES): WreckSite[] {
  return sites.filter((s) => wreckAvailable(char, s, now));
}

export function markSalvaged(char: CharacterSave, site: WreckSite, now = Date.now()): void {
  char.flags[flagKey(site.id)] = now + WRECK_RESPAWN_MS[site.kind];
}

function addItem(drop: LootDrop, id: string, n: number): void {
  if (n <= 0) return;
  const e = drop.items.find((i) => i.id === id);
  if (e) e.count += n;
  else drop.items.push({ id, count: n });
}

/**
 * What a wreck yields. Flotsam: silver, timber and odds and ends. Derelicts:
 * much more silver, refit materials (heartwood, relic fragments, starsteel),
 * sometimes gold, plus a treasure-chest roll (gear, potions).
 */
export function salvageLoot(kind: WreckKind, level: number, rng: Rng, salvageBonus = 0): LootDrop {
  const scale = (1 + Math.max(1, level) * 0.05) * (1 + Math.max(0, salvageBonus));
  const drop: LootDrop = { items: [], equip: [], silver: 0, gold: 0, cards: [] };
  if (kind === 'flotsam') {
    drop.silver = Math.round(rng.range(30, 70) * scale);
    addItem(drop, 'timber', rng.int(2, 5));
    if (rng.chance(0.4)) addItem(drop, 'ore_raw', rng.int(1, 3));
    if (rng.chance(0.25)) addItem(drop, 'fish', rng.int(1, 2));
    return drop;
  }
  drop.silver = Math.round(rng.range(150, 320) * scale);
  addItem(drop, 'timber', rng.int(4, 8));
  addItem(drop, 'ore_raw', rng.int(2, 4));
  if (rng.chance(0.7)) addItem(drop, 'relic_fragment', rng.int(1, 3));
  if (rng.chance(0.4)) addItem(drop, 'timber_rare', rng.int(1, 2));
  if (rng.chance(0.15)) addItem(drop, 'ore_rare', 1);
  if (rng.chance(0.3)) drop.gold = rng.int(3, 8);
  const chest = rollLoot('chest', { level, rng });
  drop.silver += chest.silver;
  drop.gold += chest.gold;
  for (const it of chest.items) addItem(drop, it.id, it.count);
  drop.equip.push(...chest.equip);
  drop.cards.push(...chest.cards);
  return drop;
}

/**
 * Wrecks a cannonball fired from `from` along `dir` (unit) passes within the
 * blast radius of before `range`, with the flight time to each (nearest first).
 */
export function cannonBlasts(from: V2, dir: V2, range: number, speed: number, sites: readonly WreckSite[]): { site: WreckSite; delay: number }[] {
  const out: { site: WreckSite; delay: number }[] = [];
  for (const s of sites) {
    const along = (s.x - from.x) * dir.x + (s.z - from.z) * dir.z;
    if (along < -CANNON_BLAST_RADIUS || along > range + CANNON_BLAST_RADIUS) continue;
    const t = Math.max(0, Math.min(range, along));
    const closest = { x: from.x + dir.x * t, z: from.z + dir.z * t };
    if (dist(closest, s) <= CANNON_BLAST_RADIUS) out.push({ site: s, delay: t / Math.max(1, speed) });
  }
  return out.sort((a, b) => a.delay - b.delay);
}
